import { getDatabase, ref, get, set, update, onValue, runTransaction } from "firebase/database";
import { db } from "./firebase";
import { getCompanion, getSolConverterCost, getStarterCompanionId, isCompanionUnlocked, radiantKey, getRadiantCost } from "./companions";
import { grantAchievement } from "./achievementWrites";
import { getAllGrimoireCards, CARD_TYPES } from "./cards";
import { STARTER_DECKS } from "./starterDecks";

// ─────────────────────────────────────────────────────────────
// PROFILE-KEY CHOKEPOINT — the one filter every profile write passes through.
//
// THE WIPE-CLASS BUG: a profile node is only safe when keyed by the Firebase
// Auth uid. If anything ever writes profiles/{displayName} it shadows the
// real profiles/{uid} node, and currency/collection appear to vanish. Under
// the new owner-only rules a name-keyed write also SILENTLY FAILS, so the
// player just loses the grant with no error.
//
// Rather than trust 40+ call sites to always pass a uid, every write-function
// below routes its key through _uidKey(). A Firebase Auth uid is a 28-char
// alphanumeric token. Anything that doesn't look like one (a display name, a
// literal "Aenarchist" fallback, undefined) is REFUSED here — the write never
// happens, so a name-keyed profile node is structurally impossible to create.
//
// This is intentionally strict. A refused write logs loudly and returns the
// function's safe "nothing happened" value. If you ever see this warn, the
// CALLER is passing a name where a uid belongs — fix the caller, not this.
// ─────────────────────────────────────────────────────────────
const _looksLikeUid = (k) =>
  typeof k === "string" && /^[A-Za-z0-9]{20,40}$/.test(k);

const _uidKey = (key, fnName = "profile write") => {
  if (_looksLikeUid(key)) return key;
  console.error(
    `[progression] BLOCKED non-uid profile key in ${fnName}:`,
    JSON.stringify(key),
    "— caller must pass the Firebase Auth uid, not a display name. Write refused."
  );
  return null;
};

// ─────────────────────────────────────────────────────────────
// TERROR — PROGRESSION SYSTEM
// Divine Crests, card collection, story progress
// ─────────────────────────────────────────────────────────────

// ── CONSTANTS ────────────────────────────────────────────────
export const CREST_REWARDS = {
  NPC_WIN: 75,                  // every win (Story Mode)
  FIRST_DEFEAT_BONUS: 100,      // first time defeating each story opponent
  MID_DUNGEON_BONUS: 75,        // after defeating 2nd opponent in a dungeon
  STORY_CHAPTER_CLEAR: 150,     // completing a full dungeon
  STORY_REMATCH: 5,             // re-defeating an already-beaten story opponent ("almost nothing")
  FIRST_WIN_BONUS: 100,         // very first win ever
  DAILY_LOGIN: 25,
  STORY_COMPLETE: 1500,         // beat J'ynxi — the grand finale
};

// ── ENDLESS DEPTHS DC REWARDS ──────────────────────────────────
// Small DC per win, scaled by current Elo rank
// Gatekeeper victories pay 3× the rank reward
// Loss = 0, Draw = 1 flat
export const DEPTH_DC_REWARDS = {
  escling:    1,   // 0–399 Elo
  beb:        2,   // 400–799
  champion:   3,   // 800–1199
  lord:       4,   // 1200–1699
  escendant:  5,   // 1700–2199
  aenarchist: 6,   // 2200–2799
  solrend:    8,   // 2800+
  draw:       1,   // any rank, draw
  gatekeeperMult: 3, // multiply win reward for gatekeeper fights
};

export const PACK_COSTS = {
  // ── Core Archetypes (5 DC each)
  void_eclipse:        5,
  solar_ascension:     5,
  clockwork_dominion:  5,
  dragons_wrath:       5,
  supreme_council:     5,
  instinct_rising:     5,
  unstable_world:      5,
  
  // ── Holographic Boutique — Guaranteed Foil (12-16 DC each)
  void_eclipse_mythic: 12,
  solar_ascension_gold: 12,
  clockwork_dominion_depths: 15,
  dragons_wrath_solrend: 14,
  supreme_council_aeterni: 16,
  instinct_rising_shadow: 13,
  void_eclipse_void: 13,
  unstable_world_jynxi: 14,
  
  // ── Mono-Class Power (6 DC each)
  space_ascendants:    6,
  phantom_mastery:     6,
  time_engine:         6,
  dragons_armory:      6,
  
  // ── Tome Mastery (7 DC each)
  shadow_gambler:      7,
  rituals_ascent:      7,
  nihils_embrace:      7,
  contract_broker:     7,
  
  // ── Premier Experiences (8-20 DC)
  all_stars_mythic:    20,
  depths_sanctuary:    18,
  hybrid_chaos:        9,
  affinity_ascension:  8,
  prophecy_oracle:     9,
  
  // ── The Testing Playground
  randomizer_supreme:  4,
  
  // ── Special (free)
  special:             0,
};

export const PACK_CONTENTS = {
  // 10 cards: 3 common, 2 uncommon, 4 rare, 1 holo slot (15% chance)
  standard: { total: 10, commons: 3, uncommons: 2, rares: 4, holoChance: 0.15 },
  // 12 cards: guaranteed 2 holos — drops from dungeon bosses
  special:  { total: 12, commons: 3, uncommons: 3, rares: 4, holoChance: 1.0, guaranteedHolos: 2 },
  
  // ── HOLOGRAPHIC BOUTIQUE — Guaranteed holos ──────────────────
  // Every card pulls as holographic
  holo_guaranteed: { total: 10, commons: 3, uncommons: 2, rares: 4, holoChance: 1.0 },
};

// Rarity tiers — used for pack pulls
export const RARITY = {
  COMMON: "common",
  RARE: "rare",
  HOLO_SILVER: "holo_silver",  // ◈ Silver — base holo, silver shimmer
  HOLO_GOLD: "holo_gold",      // ✦ Gold — premium holo, gold shimmer
  HOLO_MYTHIC: "holo_mythic", // ◆ Mythic — rarest, full color foil
  HOLO_VOID: "holo_void",      // ◈ Void — deep purple pulse
  HOLO_SOLREND: "holo_solrend", // ◆ Solrend — crimson fire
  HOLO_AETERNUS: "holo_aeternus", // ✦ A'eterni — celestial white
  HOLO_SHADOW: "holo_shadow",   // ◇ Shadow — invert foil
  HOLO_DEPTHS: "holo_depths",   // ◆ Depths — ocean cascade
  HOLO_JYNXI: "holo_jynxi",     // ✦ J'ynxi — trickster emerald green
  // Legacy alias
  HOLO: "holo_silver",
};

// Holo tier chances per pack slot (standard distribution)
const rollHoloTier = () => {
  const r = Math.random();
  if (r < 0.05) return RARITY.HOLO_MYTHIC;  // 5% — rarest
  if (r < 0.20) return RARITY.HOLO_GOLD;     // 15% — gold
  return RARITY.HOLO_SILVER;                  // 80% of holos — silver
};

// Extended holo tier roller — for boutique packs
const rollExtendedHoloTier = (tierList) => {
  // tierList: array of holo tier strings (e.g., ["holo_mythic", "holo_silver"])
  // Returns one of the specified tiers with equal weight
  if (!tierList || tierList.length === 0) return RARITY.HOLO_SILVER;
  return tierList[Math.floor(Math.random() * tierList.length)];
};

// Which cards belong to which pack theme
export const PACK_POOLS = {
  // ── Void Eclipse (VE): Phantom beings + Nihil + Ritual Tomes ──
  void_eclipse: {
    common: [
      "spectral_tiger_beb", "shadow_fox_beb", "inverted_serpent_beb",
      "escling_phantom",
      "tome_chao_garden", "tome_bhodi_tree", "tome_chao_duhmdumn_beb",
      "tome_nothing_left", "tome_not_here", "tome_not_now", "tome_not_ever",
      "tome_wall_of_light", "tome_seal_of_nihil", "tome_rays_of_hope",
      "tome_shower_of_lyt", "tome_soulcerers_seal",
    ],
    rare: [
      "spectral_tiger_champion", "spectral_tiger_lord",
      "shadow_fox_champion", "shadow_fox_lord",
      "inverted_serpent_champion", "inverted_serpent_lord",
      "p_spectral_tiger_1", "p_spectral_tiger_2", "p_spectral_tiger_3",
      "p_shadow_fox_1", "p_shadow_fox_2", "p_shadow_fox_3",
      "p_inverted_serpent_1", "p_inverted_serpent_2", "p_inverted_serpent_3",
      "u_spectral_tiger_lord", "u_shadow_fox_lord", "u_inverted_serpent_lord",
      "tome_monoliths_of_nihil", "tome_mausoleum_of_nihil",
      "tome_sword_shield_nihil", "tome_soulcerers_monoliths", "tome_soulcerers_mausoleum",
      "tome_shadow_blanket", "tome_inverted_knife", "tome_light_casting_shadow",
      "tome_river_of_blood", "tome_blood_in_blood_out", "tome_blood_money",
      "aff_evil", "proph_phantom_planet",
    ],
  },

  // ── Solar Ascension (SA): Space beings + Construction + Treasure Tomes ──
  solar_ascension: {
    common: [
      "h01_beb", "h09_beb", "xulan_beb",
      "escling_space",
      "tome_chao_garden", "tome_bhodi_tree", "tome_chao_duhmdumn_beb",
      "tome_nothing_left", "tome_not_here", "tome_wall_of_light",
      "tome_stars_and_skies", "tome_anima_house", "tome_gauntlets_azka",
    ],
    rare: [
      "h01_champion", "h01_lord",
      "h09_champion", "h09_lord",
      "xulan_champion", "xulan_lord",
      "p_h01_1", "p_h01_2", "p_h01_3",
      "p_h09_1", "p_h09_2", "p_h09_3",
      "p_xulan_1", "p_xulan_2", "p_xulan_3",
      "u_h01_lord", "u_h09_lord", "u_xulan_lord",
      "tome_chao_arcadia", "tome_chao_paradise",
      "tome_boots_wing_walking", "tome_lyt_footed",
      "tome_necklace_death", "tome_necklace_courage", "tome_necklace_akula",
      "tome_chief_calidum", "tome_lightning_blade",
      "aff_holy", "proph_space_race",
    ],
  },

  // ── Clockwork Dominion (CD): Time beings + Omnia + Contract Tomes ──
  clockwork_dominion: {
    common: [
      "z4z_beb", "dg0_beb", "antdroid_beb",
      "escling_time",
      "tome_chao_garden", "tome_bhodi_tree", "tome_chao_duhmdumn_beb",
      "tome_nothing_left", "tome_not_here", "tome_wall_of_light",
      "tome_seal_of_omnis", "tome_soulcerers_seal",
      "tome_agree_to_disagree", "tome_restraining_ordeal",
    ],
    rare: [
      "z4z_champion", "z4z_lord",
      "dg0_champion", "dg0_lord",
      "antdroid_champion", "antdroid_lord",
      "p_z4z_1", "p_z4z_2", "p_z4z_3",
      "p_dg0_1", "p_dg0_2", "p_dg0_3",
      "p_antdroid_1", "p_antdroid_2", "p_antdroid_3",
      "u_z4z_lord", "u_dg0_lord", "u_antdroid_lord",
      "tome_monoliths_of_omnis", "tome_mausoleum_of_omnis",
      "tome_sword_shield_omnis", "tome_omnis_corruption",
      "tome_devils_advocate", "tome_judge_jury", "tome_solomon_godfry",
      "tome_regnant", "tome_superque", "tome_all_or_nothing",
      "aff_awoken", "proph_time_to_pretend",
    ],
  },

  // ── Dragon's Wrath (DW): Dragon beings + Ancient Sorcery + Shadow Gambling ──
  dragons_wrath: {
    common: [
      "lucky_dragon_beb", "premier_dragon_beb", "chaos_dragon_beb",
      "escling_dragon",
      "tome_chao_garden", "tome_bhodi_tree", "tome_chao_duhmdumn_beb",
      "tome_nothing_left", "tome_not_here", "tome_wall_of_light",
      "tome_temptation_manifested", "tome_snake_eyes",
    ],
    rare: [
      "lucky_dragon_champion", "lucky_dragon_lord",
      "premier_dragon_champion", "premier_dragon_lord",
      "chaos_dragon_champion", "chaos_dragon_lord",
      "p_lucky_dragon_1", "p_lucky_dragon_2", "p_lucky_dragon_3",
      "p_premier_dragon_1", "p_premier_dragon_2", "p_premier_dragon_3",
      "p_chaos_dragon_1", "p_chaos_dragon_2", "p_chaos_dragon_3",
      "u_lucky_dragon_lord", "u_premier_dragon_lord", "u_chaos_dragon_lord",
      "tome_sword_of_shadows", "tome_weeping_dagger", "tome_inverted_knife",
      "tome_casino_royale", "tome_dragon_deyes", "tome_gambits_lucky_cat",
      "tome_nevermore", "tome_underdogs_howl", "tome_daft_fantasy",
      "tome_goodbye_horses", "tome_monkey_suite", "tome_magma_carta",
      "tome_king_of_the_castle", "tome_seize_and_disease",
      "aff_jynxi", "proph_dragonforce",
    ],
  },

  // ── Supreme Council (SC): All U'hltis + Affinities + Prophecies + Curios ──
  supreme_council: {
    common: [
      "curio_wizards_cap", "curio_circle_of_lyf", "curio_jynxi_wares",
      "curio_spectacles", "curio_mages_amulet", "curio_elixir",
      "curio_warlocks_robe", "curio_beb_buddy", "curio_badass_beb",
      "aff_holy", "aff_evil", "aff_awoken", "aff_jynxi",
    ],
    rare: [
      "u_h01_lord", "u_h09_lord", "u_xulan_lord",
      "u_spectral_tiger_lord", "u_shadow_fox_lord", "u_inverted_serpent_lord",
      "u_z4z_lord", "u_dg0_lord", "u_antdroid_lord",
      "u_lucky_dragon_lord", "u_premier_dragon_lord", "u_chaos_dragon_lord",
      "proph_space_race", "proph_phantom_planet", "proph_dragonforce",
      "proph_time_to_pretend", "proph_tablet_omnis", "proph_tablet_nullus",
      "proph_ace_of_shades", "proph_bloody_hell",
      "tome_divine_justice", "tome_cleansing_burst", "tome_phoenix_aura",
      "tome_radiant_aura", "tome_radiant_armor", "tome_wall_of_life",
      "tome_lyt_hearted",
    ],
  },

  // ── Instinct Rising (IR): All 36 Primeras + Curios ──
  instinct_rising: {
    common: [
      "p_h01_1", "p_h09_1", "p_xulan_1",
      "p_spectral_tiger_1", "p_shadow_fox_1", "p_inverted_serpent_1",
      "p_z4z_1", "p_dg0_1", "p_antdroid_1",
      "p_lucky_dragon_1", "p_premier_dragon_1", "p_chaos_dragon_1",
      "curio_wizards_cap", "curio_circle_of_lyf", "curio_spectacles",
      "curio_mages_amulet", "curio_elixir", "curio_beb_buddy",
    ],
    rare: [
      "p_h01_2", "p_h01_3",
      "p_h09_2", "p_h09_3",
      "p_xulan_2", "p_xulan_3",
      "p_spectral_tiger_2", "p_spectral_tiger_3",
      "p_shadow_fox_2", "p_shadow_fox_3",
      "p_inverted_serpent_2", "p_inverted_serpent_3",
      "p_z4z_2", "p_z4z_3",
      "p_dg0_2", "p_dg0_3",
      "p_antdroid_2", "p_antdroid_3",
      "p_lucky_dragon_2", "p_lucky_dragon_3",
      "p_premier_dragon_2", "p_premier_dragon_3",
      "p_chaos_dragon_2", "p_chaos_dragon_3",
      "curio_jynxi_wares", "curio_warlocks_robe", "curio_badass_beb",
      "proph_bloody_hell",
      "tome_chao_duhmdumn_champion", "tome_chao_duhmdumn_lord",
    ],
  },

  // ── Unstable World (UW): All L1 beings + U'hltis + Shadow Gambling + Affinities + Esclings ──
  unstable_world: {
    common: [
      "h01_beb", "h09_beb", "xulan_beb",
      "spectral_tiger_beb", "shadow_fox_beb", "inverted_serpent_beb",
      "z4z_beb", "dg0_beb", "antdroid_beb",
      "lucky_dragon_beb", "premier_dragon_beb", "chaos_dragon_beb",
      "escling_space", "escling_phantom", "escling_time", "escling_dragon",
      "aff_holy", "aff_evil", "aff_awoken", "aff_jynxi",
      "tome_temptation_manifested", "tome_snake_eyes",
      "tome_devils_advocate", "tome_agree_to_disagree",
      "tome_chao_garden", "tome_chao_duhmdumn_beb",
    ],
    rare: [
      "u_h01_lord", "u_h09_lord", "u_xulan_lord",
      "u_spectral_tiger_lord", "u_shadow_fox_lord", "u_inverted_serpent_lord",
      "u_z4z_lord", "u_dg0_lord", "u_antdroid_lord",
      "u_lucky_dragon_lord", "u_premier_dragon_lord", "u_chaos_dragon_lord",
      "tome_casino_royale", "tome_dragon_deyes", "tome_gambits_lucky_cat",
      "tome_nevermore", "tome_daft_fantasy", "tome_goodbye_horses",
      "tome_monkey_suite", "tome_king_of_the_castle",
      "proph_ace_of_shades", "proph_tablet_omnis", "proph_tablet_nullus",
      "curio_badass_beb", "curio_beb_buddy", "curio_jynxi_wares",
    ],
  },

  // ── Legacy aliases → resolved via resolvePool() ──
  space:   null,
  phantom: null,
  time:    null,
  dragon:  null,
};

// Resolve pool — maps old IDs and new IDs, fallback to unstable_world
const PACK_ID_MAP = {
  // Legacy shortcuts
  space:   "solar_ascension",
  phantom: "void_eclipse",
  time:    "clockwork_dominion",
  dragon:  "dragons_wrath",
  
  // ── Holo Boutique packs route to base pools ──────────────────
  void_eclipse_mythic: "void_eclipse",
  void_eclipse_void: "void_eclipse",
  solar_ascension_gold: "solar_ascension",
  clockwork_dominion_depths: "clockwork_dominion",
  dragons_wrath_solrend: "dragons_wrath",
  supreme_council_aeterni: "supreme_council",
  instinct_rising_shadow: "instinct_rising",
  unstable_world_jynxi: "unstable_world",
  
  // ── Mono-class packs ──────────────────────────────────────────
  space_ascendants: "solar_ascension",
  phantom_mastery: "void_eclipse",
  time_engine: "clockwork_dominion",
  dragons_armory: "dragons_wrath",
  
  // ── Tome mastery packs ────────────────────────────────────────
  shadow_gambler: "dragons_wrath",
  rituals_ascent: "void_eclipse",
  nihils_embrace: "void_eclipse",
  contract_broker: "clockwork_dominion",
  
  // ── Premier experiences ───────────────────────────────────────
  all_stars_mythic: "unstable_world",
  depths_sanctuary: "void_eclipse",
  hybrid_chaos: "unstable_world",
  affinity_ascension: "supreme_council",
  prophecy_oracle: "supreme_council",
  
  // ── The testing playground ───────────────────────────────────
  randomizer_supreme: "unstable_world",
};
const resolvePool = (packType) => {
  const id = PACK_ID_MAP[packType] || packType;
  return PACK_POOLS[id] || PACK_POOLS.unstable_world;
};

// Public resolver — the shop uses this to show exactly which cards a
// pack can contain. Returns { common: [...ids], rare: [...ids] }.
// Works for every pack id (base, boutique, mono, tome, premier, chaos).
export const getPackCardPool = (packType) => {
  const pool = resolvePool(packType);
  return { common: pool.common || [], rare: pool.rare || [] };
};

// Special dungeon pack mascot holographics
export const DUNGEON_HOLO_MASCOTS = {
  dungeon_1: {
    h01: "h01_lord",
    h09: "h09_lord",
    xulan: "xulan_lord",
    spectral_tiger: "spectral_tiger_lord",
    shadow_fox: "shadow_fox_lord",
    inverted_serpent: "inverted_serpent_lord",
    lucky_dragon: "lucky_dragon_lord",
    premier_dragon: "premier_dragon_lord",
    chaos_dragon: "chaos_dragon_lord",
    z4z: "z4z_lord",
    dg0: "dg0_lord",
    antdroid: "antdroid_lord",
  },
};

// ── PLAYER PROFILE ────────────────────────────────────────────
// Key-agnostic profile read. Callers pass the Firebase Auth uid
// (profiles are keyed by uid — see createProfile in Plaza.js).
export const getPlayerProfile = async (uid) => {
  uid = _uidKey(uid, "getPlayerProfile");
  if (!uid) return null;
  const r = ref(db, `profiles/${uid}`);
  const snap = await get(r);
  return snap.exists() ? snap.val() : null;
};

// Key-agnostic live profile listener. Callers pass the uid.
export const listenToProfile = (uid, callback) => {
  const r = ref(db, `profiles/${uid}`);
  return onValue(r, snap => callback(snap.exists() ? snap.val() : null));
};

// ── LEADERBOARD MIRROR ────────────────────────────────────────
// Profiles are owner-private (Firebase rules lock profiles/$uid to
// auth.uid). The Plaza leaderboard needs cross-user reads, so we
// denormalize the two public-safe fields — name + wins — into a
// world-readable /leaderboard/$uid node. Called after any totalWins
// change. Owner-write, world-read (see firebase rules).
const mirrorLeaderboard = async (uid, name, wins) => {
  if (!uid) return;
  try {
    await update(ref(db, `leaderboard/${uid}`), {
      name: name || "Aenarchist",
      wins: wins || 0,
      updatedAt: Date.now(),
    });
  } catch (e) {
    // Non-blocking — a leaderboard mirror failure must never break a win.
    console.warn("[mirrorLeaderboard] failed:", e);
  }
};

// ── DIVINE CRESTS ─────────────────────────────────────────────

// HARDENED: divineCrests is now mutated via an atomic transaction on the
// balance node itself — a delta, never a full-object overwrite. Two tabs /
// a network blip can no longer read-then-clobber the balance. The audit
// log is appended separately (push-style key, never overwrites siblings).
export const awardCrests = async (uid, amount, reason = "") => {
  uid = _uidKey(uid, "awardCrests");
  if (!uid) return;
  const balRef = ref(db, `profiles/${uid}/divineCrests`);
  const result = await runTransaction(balRef, (current) =>
    Math.max(0, (current || 0) + amount)
  );
  if (!result.committed) return;
  // Append-only audit entry — separate write, cannot disturb the balance.
  await update(ref(db, `profiles/${uid}`), {
    [`crestHistory/${Date.now()}`]: { amount, reason },
  });
  return result.snapshot.val();
};

// ── SWEET TREAT ───────────────────────────────────────────────
// Plaza-Willy's first-visit grant. Atomic write of crests + flag
// in a single update() call so a network blip mid-grant cannot
// double-pay. Idempotent — refuses if already claimed.
//
// Returns { newTotal, alreadyClaimed }:
//   - { newTotal: <number>, alreadyClaimed: false }  on grant
//   - { newTotal: null,     alreadyClaimed: true  }  if already received
//   - null                                            if no profile (shouldn't happen)
export const awardSweetTreat = async (uid) => {
  uid = _uidKey(uid, "awardSweetTreat");
  if (!uid) return null;
  const SWEET_TREAT_AMOUNT = 5;
  const stamp = Date.now();

  // HARDENED: the read (idempotency check) and the write (crests + flag +
  // audit) now happen inside ONE transaction on the profile node, so the
  // "already claimed?" check and the payout can never be split by a race.
  const result = await runTransaction(
    ref(db, `profiles/${uid}`),
    (profile) => {
      if (!profile) return profile;                 // no profile — abort, leave untouched
      if (profile.hasReceivedSweetTreat) return;    // already claimed — abort transaction
      profile.divineCrests = (profile.divineCrests || 0) + SWEET_TREAT_AMOUNT;
      profile.hasReceivedSweetTreat = true;
      profile.crestHistory = profile.crestHistory || {};
      profile.crestHistory[stamp] = {
        amount: SWEET_TREAT_AMOUNT,
        reason: "first_visit_sweet_treat",
      };
      return profile;
    }
  );

  if (!result.committed || !result.snapshot.exists()) {
    return { newTotal: null, alreadyClaimed: true };
  }
  return { newTotal: result.snapshot.val().divineCrests, alreadyClaimed: false };
};

export const saveLoadout = async (uid, loadout) => {
  // loadout: { grimoire, luxmoire, prophecy, curios }
  uid = _uidKey(uid, "saveLoadout");
  if (!uid) return false;
  const profileRef = ref(db, `profiles/${uid}`);
  await update(profileRef, { loadout });
  return true;
};

// HARDENED: check-balance-and-debit is now a single atomic transaction on
// the divineCrests node. The old read-then-write could let two purchases
// both pass the "enough crests?" check and double-spend, or clobber a
// concurrent award. The transaction aborts (returns undefined) when the
// balance is insufficient, so spendCrests still returns false correctly.
export const spendCrests = async (uid, amount) => {
  uid = _uidKey(uid, "spendCrests");
  if (!uid || amount <= 0) return false;
  const balRef = ref(db, `profiles/${uid}/divineCrests`);
  const result = await runTransaction(balRef, (current) => {
    const bal = current || 0;
    if (bal < amount) return;          // abort — not enough, leave balance untouched
    return bal - amount;
  });
  return result.committed && result.snapshot.exists();
};

// ── COLLECTION ────────────────────────────────────────────────

// HARDENED — this is the function behind the holo-wipe.
// The old version did get() → set(). If the get() ever returned empty for a
// card that actually existed (offline cache miss, second tab, network blip),
// the set() wrote a fresh {count:1} object and DESTROYED holoCount/holoTier.
// Now it's a single atomic transaction on the card node: the increment is
// always applied to whatever the server currently holds, so existing holo
// data is mathematically preserved. The transaction merges onto `current`
// rather than building a fresh object, so any future fields survive too.
// `source` is the PROVENANCE id (see SourceSigil.SOURCE_SIGILS): where this
// specific card came from — "earned", "vault", "booster_shadowfox", etc.
// It is APPEND-ONLY, two ways:
//   sources      : {sourceId: count}            — how many of this card from
//                                                  each source, foil or not.
//   sourceTiers  : {tier: {sourceId: count}}    — the per-FOIL provenance the
//                                                  Forge reads: "Solrend ×1
//                                                  from Shadow Fox, Solrend ×1
//                                                  from Vault." This is what
//                                                  puts the NUMBER on a foil
//                                                  swatch and fills its picker.
// A non-holo grant records under `sources` only (it has no tier). A holo grant
// records under both. Default "" source = legacy/unknown grant; we DON'T stamp
// an empty source into the maps (keeps old call sites clean), so a card with no
// recorded provenance simply has no sigil data and the viewer shows fallback.
export const addCardToCollection = async (uid, cardId, isHolo = false, holoTier = null, source = "") => {
  uid = _uidKey(uid, "addCardToCollection");
  if (!uid || !cardId) return;
  const cardRef = ref(db, `profiles/${uid}/collection/${cardId}`);
  await runTransaction(cardRef, (current) => {
    const c = current || { count: 0, holoCount: 0, holoTier: null };
    // holoTiers is the SET (as a {tier: count} map) of every foil this card
    // is owned in — the per-tier truth the Forge + Foil Vault read. Append-only:
    // we only ever increment, never clear, so an existing record is never wiped.
    // Legacy `holoTier` (last foil obtained) is kept in sync for back-compat.
    const tiers = { ...(c.holoTiers || {}) };
    if (isHolo && holoTier) tiers[holoTier] = (tiers[holoTier] || 0) + 1;

    // ── PROVENANCE (append-only, same atomic transaction) ────────────
    const sources = { ...(c.sources || {}) };
    const sourceTiers = {};
    for (const [t, m] of Object.entries(c.sourceTiers || {})) {
      sourceTiers[t] = { ...(m || {}) };
    }
    if (source) {
      sources[source] = (sources[source] || 0) + 1;
      if (isHolo && holoTier) {
        const m = sourceTiers[holoTier] || (sourceTiers[holoTier] = {});
        m[source] = (m[source] || 0) + 1;
      }
    }

    return {
      ...c,
      // non-holo grant adds a count; holo grant guarantees count ≥ 1
      count: Math.max((c.count || 0) + (isHolo ? 0 : 1), isHolo ? 1 : 0),
      holoCount: isHolo ? (c.holoCount || 0) + 1 : (c.holoCount || 0),
      holoTier: holoTier || c.holoTier || null,
      holoTiers: tiers,
      sources,
      sourceTiers,
    };
  });
  // Evaluate collection-shaped achievements off the fresh state. Idempotent;
  // non-fatal — a failure here must never break a card grant.
  try { await checkCollectionAchievements(uid); } catch (_) { /* non-fatal */ }
};

export const getCollection = async (uid) => {
  uid = _uidKey(uid, "getCollection");
  if (!uid) return {};
  const snap = await get(ref(db, `profiles/${uid}/collection`));
  return snap.exists() ? snap.val() : {};
};

// ═══════════════════════════════════════════════════════════════
// PROVENANCE BACKFILL — one-time, idempotent
// ───────────────────────────────────────────────────────────────
// Cards granted BEFORE the source-sigil system shipped have no `sources`
// data, so they show no origin sigil. This stamps the player's STARTER deck
// cards (which every account owns from day one) with the `starter` source —
// but ONLY on cards that have NO source recorded yet, so it never overwrites
// real provenance (a vault/pack/earned copy is left untouched) and is safe to
// run on every Forge load. It writes append-only via update() at the exact
// child paths, never a full-object overwrite, so nothing can be wiped.
//
// `starterCardIds` is the list of card ids in the player's starter deck —
// pass getStarterDeck(starterDeckId)'s grimoire keys + curios. No-op if the
// player has no starterDeckId or the list is empty.
export const backfillStarterProvenance = async (uid, starterCardIds = []) => {
  uid = _uidKey(uid, "backfillStarterProvenance");
  if (!uid || !Array.isArray(starterCardIds) || starterCardIds.length === 0) {
    return { success: true, stamped: 0, noChange: true };
  }
  const col = await getCollection(uid);
  const updates = {};
  let stamped = 0;
  for (const cardId of starterCardIds) {
    const rec = col[cardId];
    if (!rec) continue;                       // don't own it → nothing to stamp
    const hasSource = rec.sources && Object.keys(rec.sources).length > 0;
    const hasTierSource = rec.sourceTiers && Object.keys(rec.sourceTiers).length > 0;
    if (hasSource || hasTierSource) continue; // already has provenance → leave it
    // Append-only, exact-path write — never a full-object overwrite.
    updates[`profiles/${uid}/collection/${cardId}/sources/starter`] = 1;
    stamped++;
  }
  if (stamped === 0) return { success: true, stamped: 0, noChange: true };
  await update(ref(db), updates);
  return { success: true, stamped };
};

// ═══════════════════════════════════════════════════════════════
// COLLECTION ACHIEVEMENT EVALUATOR
// ───────────────────────────────────────────────────────────────
// One evaluator, run whenever the collection changes (every card
// enters through addCardToCollection, so that's the single hook).
// It derives every collection-shaped achievement from the live
// collection + profile and fires whatever thresholds are newly met.
// grantAchievement is idempotent, so re-running on every card add is
// safe and self-healing — a player who somehow skipped a grant gets
// it the next time any card lands. No scattered per-call-site grants.
//
// Covers: collector_50 / collector_100 / collector_all, holo_5,
// jynxi_holo, five_affinity, void_collector. (all_starters needs a
// per-deck win tracker that doesn't exist yet — wired separately.)
// ═══════════════════════════════════════════════════════════════

// Built once, lazily: cardId → affinity label, and the void-pack id set.
let _affinityIndex = null;
let _voidPackIds = null;
function _buildCardIndexes() {
  if (_affinityIndex && _voidPackIds) return;
  _affinityIndex = {};
  try {
    for (const c of getAllGrimoireCards()) {
      if (c && c.id && c.affinityType) _affinityIndex[c.id] = c.affinityType;
    }
  } catch (_) { _affinityIndex = {}; }
  _voidPackIds = new Set();
  try {
    const pool = PACK_POOLS.void_eclipse || {};
    for (const tierArr of Object.values(pool)) {
      if (Array.isArray(tierArr)) tierArr.forEach((id) => _voidPackIds.add(id));
    }
  } catch (_) { _voidPackIds = new Set(); }
}

// Total unique cards in the game (denominator for collector_all).
let _totalUnique = null;
function _getTotalUnique() {
  if (_totalUnique != null) return _totalUnique;
  try { _totalUnique = getAllGrimoireCards().filter((c) => c && c.id).length; }
  catch (_) { _totalUnique = 181; } // fallback to the known catalog size
  return _totalUnique;
}

export const checkCollectionAchievements = async (uid, collection = null) => {
  uid = _uidKey(uid, "checkCollectionAchievements");
  if (!uid) return;
  _buildCardIndexes();

  // Pull the collection if not supplied (post-write callers pass null).
  let coll = collection;
  if (!coll) {
    const snap = await get(ref(db, `profiles/${uid}/collection`));
    coll = snap.exists() ? snap.val() : {};
  }
  const ids = Object.keys(coll || {});
  if (ids.length === 0) return;

  // Derived metrics
  const uniqueCount = ids.length;
  let holoCount = 0;
  let hasJynxiHolo = false;
  const affinities = new Set();
  let hasVoid = false;

  for (const id of ids) {
    const entry = coll[id] || {};
    const hc = entry.holoCount || 0;
    holoCount += hc;
    // Per-tier truth: holoTiers map first, legacy single holoTier as fallback.
    const tierMap = entry.holoTiers || null;
    const ownsJynxi = tierMap
      ? (tierMap.holo_jynxi || 0) > 0
      : (hc > 0 && entry.holoTier === "holo_jynxi");
    if (ownsJynxi) hasJynxiHolo = true;
    const aff = _affinityIndex[id];
    if (aff) affinities.add(aff);
    if (_voidPackIds.has(id)) hasVoid = true;
  }

  const total = _getTotalUnique();

  // Fire whatever is newly met. grantAchievement no-ops if already owned.
  const grants = [];
  if (uniqueCount >= 50)    grants.push(["terror:collector_50",  "collection:unique50"]);
  if (uniqueCount >= 100)   grants.push(["terror:collector_100", "collection:unique100"]);
  if (uniqueCount >= total) grants.push(["terror:collector_all", "collection:uniqueAll"]);
  if (holoCount >= 5)       grants.push(["terror:holo_5",        "collection:holo5"]);
  if (hasJynxiHolo)         grants.push(["terror:jynxi_holo",    "collection:jynxiHolo"]);
  if (affinities.size >= 3) grants.push(["terror:five_affinity", "collection:affinity3"]);
  if (hasVoid)              grants.push(["terror:void_collector", "collection:voidPack"]);

  for (const [achId, source] of grants) {
    try { await grantAchievement(uid, achId, source); } catch (_) { /* non-fatal */ }
  }
};

// ── FOIL VAULT — buy a SPECIFIC card in a SPECIFIC holo tier ──
// The deterministic path: no gamble. Player pays the vault price and
// receives exactly the card + tier requested. Writes through the same
// append-style addCardToCollection (count ≥ 1, holoCount += 1) so it
// never overwrites or wipes existing collection data.
//
// Returns { success, reason?, cardId, holoTier }.
export const buyFoilSingle = async (uid, cardId, holoTier, cost) => {
  uid = _uidKey(uid, "buyFoilSingle");
  if (!uid) return { success: false, reason: "Invalid account." };
  if (!cardId || !holoTier) return { success: false, reason: "Invalid foil." };
  const spent = await spendCrests(uid, cost);
  if (!spent) return { success: false, reason: "Not enough Divine Crests." };
  await addCardToCollection(uid, cardId, true, holoTier, "vault");
  // Audit trail — same shape as packHistory, so a future hardening pass
  // can reconcile every grant.
  await update(ref(db, `profiles/${uid}`), {
    [`foilVaultHistory/${Date.now()}`]: { cardId, holoTier, cost },
  });
  return { success: true, cardId, holoTier };
};

// ── PACK OPENING ──────────────────────────────────────────────

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Maps a packType to its provenance sigil id (see SourceSigil.SOURCE_SIGILS).
// EVERY pack now has its own named sigil ("every source" pass, Jul 2026) —
// a card pulled from any pack wears that pack's mark forever. The generic
// "booster" stamp remains only as the fallback for unknown/future packTypes
// (and as the legacy mark on cards pulled before this map was complete).
// Adding a pack = one SOURCE_SIGILS entry + one line here.
const PACK_SOURCE = {
  // Core seven
  void_eclipse:                 "booster_void_eclipse",
  solar_ascension:              "booster_solar_ascension",
  clockwork_dominion:           "booster_clockwork_dominion",
  dragons_wrath:                "booster_dragons_wrath",
  supreme_council:              "booster_supreme_council",
  instinct_rising:              "booster_instinct_rising",
  unstable_world:               "booster_unstable_world",
  // Holo editions
  void_eclipse_mythic:          "booster_void_eclipse_mythic",
  solar_ascension_gold:         "booster_solar_ascension_gold",
  clockwork_dominion_depths:    "booster_clockwork_dominion_depths",
  dragons_wrath_solrend:        "booster_dragons_wrath_solrend",
  supreme_council_aeterni:      "booster_supreme_council_aeterni",
  instinct_rising_shadow:       "booster_shadowfox", // the OG named sigil (PoC) — keeps legacy data coherent
  void_eclipse_void:            "booster_void_eclipse_void",
  unstable_world_jynxi:         "booster_unstable_world_jynxi",
  // Mono-class
  space_ascendants:             "booster_space_ascendants",
  phantom_mastery:              "booster_phantom_mastery",
  time_engine:                  "booster_time_engine",
  dragons_armory:               "booster_dragons_armory",
  // Tomes
  shadow_gambler:               "booster_shadow_gambler",
  rituals_ascent:               "booster_rituals_ascent",
  nihils_embrace:               "booster_nihils_embrace",
  contract_broker:              "booster_contract_broker",
  // Premier
  all_stars_mythic:             "booster_all_stars_mythic",
  depths_sanctuary:             "booster_depths_sanctuary",
  hybrid_chaos:                 "booster_hybrid_chaos",
  affinity_ascension:           "booster_affinity_ascension",
  prophecy_oracle:              "booster_prophecy_oracle",
  // Chaos
  randomizer_supreme:           "booster_randomizer_supreme",
};
const packSource = (packType) => PACK_SOURCE[packType] || "booster";

// ── CANONICAL CARD ORIGINS ("where is this card FROM?") ──────────────
// Personal provenance (collection sources) answers "where did YOUR copy
// come from" — but it needs an owned record. This answers the universal
// question for ANY card, any viewer, any side of the board: which
// starter grimoires carry it, which pack pools drop it, and the Vault
// (which carries every card). Computed straight from game data, so it
// can never go stale and works for opponent cards we know nothing about.
// Holo Editions share their base pack's pool, so the base mark speaks
// for the whole lineage.
export function getCardOrigins(cardId) {
  if (!cardId) return [];
  const out = [];
  if (STARTER_DECKS.some(d => (d.grimoire && d.grimoire[cardId]) > 0)) out.push("starter");
  for (const [packId, pool] of Object.entries(PACK_POOLS)) {
    if (!pool) continue; // legacy id placeholders (e.g. phantom: null)
    if ((pool.common || []).includes(cardId) || (pool.rare || []).includes(cardId)) {
      out.push(PACK_SOURCE[packId] || "booster");
    }
  }
  out.push("vault"); // the Vault carries every card in the game
  return [...new Set(out)];
}

export const openPack = async (playerName, packType, isFree = false) => {
  playerName = _uidKey(playerName, "openPack");
  if (!playerName) return null;
  // Spend crests if not free
  if (!isFree) {
    const spent = await spendCrests(playerName, PACK_COSTS[packType] || 300);
    if (!spent) return { success: false, reason: "Not enough Divine Crests" };
  }

  const isSpecial = packType === "special";
  
  // Determine if this is a boutique pack with guaranteed holos
  const BOUTIQUE_PACKS = {
    void_eclipse_mythic: ["holo_mythic", "holo_silver"],
    solar_ascension_gold: ["holo_gold", "holo_silver"],
    clockwork_dominion_depths: ["holo_depths"],
    dragons_wrath_solrend: ["holo_solrend"],
    supreme_council_aeterni: ["holo_aeternus"],
    instinct_rising_shadow: ["holo_shadow"],
    void_eclipse_void: ["holo_void"],
    unstable_world_jynxi: ["holo_jynxi"],
    depths_sanctuary: ["holo_depths"],
    all_stars_mythic: ["holo_mythic"],
  };
  
  const isBoutique = BOUTIQUE_PACKS[packType];
  const config = isBoutique ? PACK_CONTENTS.holo_guaranteed : (isSpecial ? PACK_CONTENTS.special : PACK_CONTENTS.standard);
  const pool = resolvePool(packType);
  const holoTierList = BOUTIQUE_PACKS[packType] || null;
  const src = packSource(packType); // provenance sigil for every card in this pack

  const cards = [];

  // Draw commons
  for (let i = 0; i < config.commons; i++) {
    const cardId = pickRandom(pool.common);
    const isHolo = isBoutique ? true : (!isSpecial && Math.random() < config.holoChance);
    const holoTier1 = isHolo ? (holoTierList ? rollExtendedHoloTier(holoTierList) : rollHoloTier()) : null;
    cards.push({ cardId, isHolo, holoTier: holoTier1, rarity: holoTier1 || RARITY.COMMON, source: src });
    await addCardToCollection(playerName, cardId, isHolo, holoTier1, src);
  }

  // Draw uncommons (from common pool, slightly better)
  for (let i = 0; i < (config.uncommons || 0); i++) {
    const cardId = pickRandom([...pool.common, ...pool.rare].slice(0, Math.ceil(pool.rare.length * 0.5)));
    const isHolo = isBoutique ? true : (!isSpecial && Math.random() < config.holoChance);
    const holoTier2 = isHolo ? (holoTierList ? rollExtendedHoloTier(holoTierList) : rollHoloTier()) : null;
    cards.push({ cardId, isHolo, holoTier: holoTier2, rarity: holoTier2 || RARITY.RARE, source: src });
    await addCardToCollection(playerName, cardId, isHolo, holoTier2, src);
  }

  // Draw rares
  for (let i = 0; i < config.rares; i++) {
    const cardId = pickRandom(pool.rare);
    const isHolo = isBoutique ? true : (!isSpecial && Math.random() < config.holoChance);
    const holoTierR = isHolo ? (holoTierList ? rollExtendedHoloTier(holoTierList) : rollHoloTier()) : null;
    cards.push({ cardId, isHolo, holoTier: holoTierR, rarity: holoTierR || RARITY.RARE, source: src });
    await addCardToCollection(playerName, cardId, isHolo, holoTierR, src);
  }

  // Guaranteed holos for special packs
  if (isSpecial) {
    const holosNeeded = config.guaranteedHolos || 1;
    for (let i = 0; i < holosNeeded; i++) {
      const holoCard = pickRandom(pool.rare);
      cards.push({ cardId: holoCard, isHolo: true, holoTier: RARITY.HOLO_MYTHIC, rarity: RARITY.HOLO_MYTHIC, source: src });
      await addCardToCollection(playerName, holoCard, true, RARITY.HOLO_MYTHIC, src);
    }
  }

  // Track pack opening
  await update(ref(db, `profiles/${playerName}`), {
    [`packHistory/${Date.now()}`]: { packType, cards: cards.map(c => c.cardId) },
  });

  // Achievements: first pack ever, and first holo
  const profileRef = ref(db, `profiles/${playerName}`);
  const profileSnap = await get(profileRef);
  const profile = profileSnap.val() || {};
  const packHistoryCount = Object.keys(profile.packHistory || {}).length;
  
  if (packHistoryCount === 1) {
    await grantAchievement(playerName, "terror:first_pack", "boutique:openPack");
  }
  
  // First holo check: if any card in this pack is holo, grant first_holo if earned
  const hasHolo = cards.some(c => c.isHolo);
  if (hasHolo) {
    await grantAchievement(playerName, "terror:first_holo", "boutique:openPack");
  }

  return { success: true, cards };
};

// ── SPECIAL DUNGEON PACK ──────────────────────────────────────
// Awarded when a dungeon is cleared — includes guaranteed holo mascot

export const openDungeonPack = async (playerName, dungeonId, starterDeckId) => {
  playerName = _uidKey(playerName, "openDungeonPack");
  if (!playerName) return null;
  const pool = PACK_POOLS.space; // mixed pool for dungeon packs
  const allPool = [
    ...resolvePool('solar_ascension').common, ...resolvePool('solar_ascension').rare,
    ...resolvePool('void_eclipse').common, ...resolvePool('void_eclipse').rare,
    ...resolvePool('clockwork_dominion').common, ...resolvePool('clockwork_dominion').rare,
    ...resolvePool('dragons_wrath').common, ...resolvePool('dragons_wrath').rare,
  ];

  const cards = [];

  // 7 random cards from all pools
  for (let i = 0; i < 7; i++) {
    const cardId = pickRandom(allPool);
    const isHolo = Math.random() < 0.2;
    cards.push({ cardId, isHolo, rarity: isHolo ? RARITY.HOLO : RARITY.RARE, source: "earned" });
    await addCardToCollection(playerName, cardId, isHolo, isHolo ? rollHoloTier() : null, "earned");
  }

  // Guaranteed holo mascot based on starter deck
  const mascots = DUNGEON_HOLO_MASCOTS[`dungeon_${dungeonId}`] || {};
  const deckKey = starterDeckId?.replace(/_deck$/, "").replace(/_/g, "_") || "h01";
  const mascotId = mascots[deckKey] || mascots["h01"] || "h01_lord";
  cards.push({ cardId: mascotId, isHolo: true, rarity: RARITY.HOLO, isMascot: true, source: "earned" });
  await addCardToCollection(playerName, mascotId, true, null, "earned");

  return { success: true, cards };
};

// ── STORY PROGRESS ────────────────────────────────────────────

export const recordStoryWin = async (playerName, opponentId, dungeonId) => {
  playerName = _uidKey(playerName, "recordStoryWin");
  if (!playerName) return;
  const profileRef = ref(db, `profiles/${playerName}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return;
  const profile = snap.val();

  const defeated = profile.storyProgress?.defeatedOpponents || [];
  const alreadyDefeated = defeated.includes(opponentId);
  const newDefeated = [...new Set([...defeated, opponentId])];

  const updates = {
    totalWins: (profile.totalWins || 0) + 1,
    "storyProgress/defeatedOpponents": newDefeated,
  };

  // Award crests.
  // Rematch rule (D1 completion wave): re-defeating an opponent the
  // player has ALREADY beaten pays STORY_REMATCH ("almost nothing") —
  // dungeons stay re-enterable without becoming a crest farm.
  let crests;
  if (alreadyDefeated) {
    crests = CREST_REWARDS.STORY_REMATCH;
  } else {
    crests = CREST_REWARDS.NPC_WIN + CREST_REWARDS.FIRST_DEFEAT_BONUS;

    // Mid-dungeon bonus — awarded when this FIRST-defeat brings the
    // player to exactly 2 downed opponents in this dungeon. (This was
    // documented in recordDungeonClear's comment but never implemented —
    // wired for real in the D1 completion wave.)
    if (dungeonId != null) {
      try {
        const { OPPONENTS } = await import("./storyData");
        const inDungeon = Object.values(OPPONENTS)
          .filter(o => o.dungeonId === dungeonId)
          .map(o => o.id);
        const downedHere = inDungeon.filter(id => newDefeated.includes(id)).length;
        if (downedHere === 2) crests += CREST_REWARDS.MID_DUNGEON_BONUS;
      } catch (_) { /* non-fatal — bonus simply skipped */ }
    }
  }

  updates.divineCrests = (profile.divineCrests || 0) + crests;

  await update(profileRef, updates);

  // Mirror to public leaderboard (name + new win total)
  await mirrorLeaderboard(playerName, profile.name, (profile.totalWins || 0) + 1);

  // First blood achievement
  if ((profile.totalWins || 0) === 0) {
    await grantAchievement(playerName, "terror:first_blood", "storyMode:win");
  }

  return { crests, firstDefeat: !alreadyDefeated };
};

export const recordDungeonClear = async (playerName, dungeonId) => {
  playerName = _uidKey(playerName, "recordDungeonClear");
  if (!playerName) return;
  const profileRef = ref(db, `profiles/${playerName}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return;
  const profile = snap.val();

  const completed = profile.storyProgress?.completedDungeons || [];
  if (completed.includes(dungeonId)) return { alreadyCleared: true };

  await update(profileRef, {
    "storyProgress/completedDungeons": [...completed, dungeonId],
    "storyProgress/currentDungeon": dungeonId + 1,
    divineCrests: (profile.divineCrests || 0) + CREST_REWARDS.STORY_CHAPTER_CLEAR,
    // Note: mid-dungeon bonus (after 2nd opponent) is awarded in recordStoryWin
  });

  // First dungeon clear
  if (completed.length === 0) {
    await grantAchievement(playerName, "terror:dungeon_clear", "storyMode:dungeonClear");
  }

  return { alreadyCleared: false, crests: CREST_REWARDS.STORY_CHAPTER_CLEAR };
};

// ═══════════════════════════════════════════════════════════════
// DUNGEON SECRETS — the persistent "easter eggs found" truth
// ───────────────────────────────────────────────────────────────
// Every collectible secret in a dungeon (crest caches, chests,
// grimoire pages, the Oath Tablet) is stamped to Firebase at
// `profiles/{uid}/storyProgress/dungeonSecrets/d{dungeonId}/{secretId}`.
// This node is the SOURCE OF TRUTH the dungeon HUD counter,
// reward grants, and the all-secrets achievement derive from —
// it survives localStorage wipes and device changes, unlike the
// dungeon's local run state.
//
// Write pattern: runTransaction on the exact stamp path — atomic
// idempotence. A stamp that already exists ABORTS the transaction,
// so crests/cards can never be double-granted even on a double-tap.
// Append-only: stamps are never deleted.
//
// ⚠ RULES-HARDENING NOTE: whitelist
//   `storyProgress/dungeonSecrets/$d/$secretId`
// under profiles when the profile node gets its $extra:false pass.
// ═══════════════════════════════════════════════════════════════

// Canonical secret roster per dungeon — the HUD "✶ X/Y" counter and
// the all-secrets achievement both count against THIS list. Trimming
// what counts as an "egg" is a one-line edit here.
export const DUNGEON_SECRET_IDS = {
  1: [
    "veil_cache",         // B1F hidden crest stash
    "veil_chest",         // B1F dead-end chest
    "grimoire_page_1",    // B2F hidden page
    "drift_secret_chest", // B2F secret room — S'uhnker Story Variant
    "grimoire_page_2",    // SANCTUM hidden page
    "sanctum_chest",      // SANCTUM post-boss Warden's Chest
    "easter_egg_tablet",  // Oath Tablet — Chaos Dragon Easter Egg Variant
  ],
};

// Record a found secret. Grants its rewards exactly once, ever.
//   opts.crests — Divine Crests to award (delta transaction via awardCrests)
//   opts.card   — { id, holoTier } to grant via addCardToCollection
// Returns { alreadyFound } (or undefined on bad uid).
export const recordDungeonSecret = async (uid, dungeonId, secretId, opts = {}) => {
  uid = _uidKey(uid, "recordDungeonSecret");
  if (!uid || !secretId || dungeonId == null) return;
  const { crests = 0, card = null } = opts;

  const stampRef = ref(db, `profiles/${uid}/storyProgress/dungeonSecrets/d${dungeonId}/${secretId}`);
  const result = await runTransaction(stampRef, (current) =>
    current ? undefined : Date.now()   // undefined = abort (already stamped)
  );
  if (!result.committed) return { alreadyFound: true };

  // Rewards — first find only (guaranteed by the transaction above).
  if (crests > 0) {
    await awardCrests(uid, crests, `dungeon${dungeonId}:${secretId}`);
  }
  if (card?.id) {
    await addCardToCollection(uid, card.id, !!card.holoTier, card.holoTier || null,
      `dungeon${dungeonId}:${secretId}`);
  }

  // Oath Tablet — hidden achievement.
  if (secretId === "easter_egg_tablet") {
    try { await grantAchievement(uid, "terror:oath_tablet", `dungeon${dungeonId}:tablet`); }
    catch (_) { /* non-fatal */ }
  }

  // All-secrets achievement — evaluated off the fresh stamp map.
  const roster = DUNGEON_SECRET_IDS[dungeonId] || [];
  if (roster.length) {
    try {
      const snap = await get(ref(db, `profiles/${uid}/storyProgress/dungeonSecrets/d${dungeonId}`));
      const map = snap.exists() ? snap.val() : {};
      if (roster.every(id => map[id])) {
        await grantAchievement(uid, `terror:d${dungeonId}_all_secrets`, "dungeon:allSecrets");
      }
    } catch (_) { /* non-fatal */ }
  }

  return { alreadyFound: false };
};

export const recordJynxiDefeat = async (playerName) => {
  playerName = _uidKey(playerName, "recordJynxiDefeat");
  if (!playerName) return;
  const profileRef = ref(db, `profiles/${playerName}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return;
  const profile = snap.val();
  if (profile.storyProgress?.jynxiDefeated) return { alreadyDefeated: true };
  await update(profileRef, {
    "storyProgress/jynxiDefeated": true,
    divineCrests: (profile.divineCrests || 0) + CREST_REWARDS.STORY_COMPLETE,
  });
  
  // Story complete achievement + beat J'ynxi
  await grantAchievement(playerName, "terror:beat_jynxi", "storyMode:jynxiDefeat");
  await grantAchievement(playerName, "terror:story_complete", "storyMode:jynxiDefeat");
  
  return { crests: CREST_REWARDS.STORY_COMPLETE };
};

// Records a pure free-play (NPC) win. Optional `starterDeckId`: when the
// win was played with a PRESET starter deck (Plaza's starter chooser
// threads it through the match meta), the deck is stamped into an
// append-only `starterWins/{deckId}: true` map on the profile — the
// tracker behind terror:all_starters (grants once EVERY deck in
// STARTER_DECKS has a stamped win). Exact-child-path write, never a
// full-map overwrite; delta pattern per the Firebase discipline.
// NOTE for the rules-hardening session: whitelist `starterWins/$deckId`
// under profiles when the profile node gets its $extra:false pass.
export const recordFreePlayWin = async (playerName, starterDeckId = null) => {
  playerName = _uidKey(playerName, "recordFreePlayWin");
  if (!playerName) return;
  const profileRef = ref(db, `profiles/${playerName}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return;
  const profile = snap.val();

  const isFirst = (profile.totalWins || 0) === 0;
  const crests = CREST_REWARDS.NPC_WIN + (isFirst ? CREST_REWARDS.FIRST_WIN_BONUS : 0);
  const freePlayWins = (profile.freePlayWins || 0) + 1;

  await update(profileRef, {
    totalWins: (profile.totalWins || 0) + 1,
    freePlayWins,
    divineCrests: (profile.divineCrests || 0) + crests,
  });

  // Mirror to public leaderboard (name + new win total)
  await mirrorLeaderboard(playerName, profile.name, (profile.totalWins || 0) + 1);

  // First freeplay win
  if (freePlayWins === 1) {
    await grantAchievement(playerName, "terror:first_freeplay", "freePlay:win");
  }
  // 50 free-play wins — the Free Spirit. Idempotent.
  if (freePlayWins >= 50) {
    try { await grantAchievement(playerName, "terror:freeplay_50", "freePlay:win50"); }
    catch (_) { /* non-fatal */ }
  }

  // ── Starter Master tracker (terror:all_starters) ─────────────
  // Stamp the exact deck's win (append-only), then evaluate completion
  // per caP'n's canon ruling (July 3): "the four starter decks" means
  // the four starter deck TYPES — the Ilks. There are 12 preset decks,
  // 3 per Ilk (Space / Phantom / Time / Dragon); the achievement grants
  // once the player has a win with at least one deck OF EACH ILK.
  // Per-deck stamps are kept (richer data than per-ilk — future-proof
  // for a "win with all 12" prestige tier if wanted). grantAchievement
  // is idempotent, so re-evaluating on every stamped win is safe.
  if (starterDeckId && STARTER_DECKS.some(d => d.id === starterDeckId)) {
    try {
      await update(ref(db, `profiles/${playerName}/starterWins`), {
        [starterDeckId]: true,
      });
      const stamped = { ...(profile.starterWins || {}), [starterDeckId]: true };
      const ilksWon = new Set(
        STARTER_DECKS.filter(d => stamped[d.id]).map(d => d.ilk)
      );
      const allIlks = new Set(STARTER_DECKS.map(d => d.ilk));
      const allWon = [...allIlks].every(ilk => ilksWon.has(ilk));
      if (allWon) {
        await grantAchievement(playerName, "terror:all_starters", "freePlay:allStarters");
      }
    } catch (_) { /* non-fatal — never break a recorded win */ }
  }

  return { crests, isFirstWin: isFirst };
};

// ─────────────────────────────────────────────────────────────
// ENDLESS DEPTHS — ELO & DEPTH PROGRESS
// ─────────────────────────────────────────────────────────────

export const DEPTH_ELO_CONFIG = {
  BASE_WIN:    28,
  BASE_LOSS:   18,
  DRAW:         5,
  DIFF_MULT: { Beb: 0.8, Champion: 1.0, Lord: 1.3 },
};

export async function loadDepthProfile(uid) {
  const snap = await get(ref(db, `profiles/${uid}/endlessDepths`));
  return snap.exists() ? snap.val() : {
    elo: 0, peakElo: 0, wins: 0, losses: 0, draws: 0,
    streak: 0, bestStreak: 0, defeatedGatekeepers: [], history: [],
  };
}

export async function recordDepthResult(uid, result, opponentName, isGatekeeper, opponentDifficulty, ctx = {}) {
  const depthRef = ref(db, `profiles/${uid}/endlessDepths`);
  const snap = await get(depthRef);
  const current = snap.exists() ? snap.val() : {};

  const mult = DEPTH_ELO_CONFIG.DIFF_MULT[opponentDifficulty] || 1;
  const delta = result === "win"  ?  Math.round(DEPTH_ELO_CONFIG.BASE_WIN  * mult)
              : result === "loss" ? -Math.round(DEPTH_ELO_CONFIG.BASE_LOSS * mult)
              : -DEPTH_ELO_CONFIG.DRAW;

  const newElo    = Math.max(0, (current.elo || 0) + delta);
  const newPeak   = Math.max(newElo, current.peakElo || 0);
  const newStreak = result === "win" ? (current.streak || 0) + 1 : 0;

  const entry = {
    t: Date.now(), result, opponentName, eloChange: delta, newElo, isGatekeeper: !!isGatekeeper,
  };

  await update(ref(db, `profiles/${uid}/endlessDepths`), {
    elo: newElo, peakElo: newPeak,
    wins:    (current.wins || 0)   + (result === "win"  ? 1 : 0),
    losses:  (current.losses || 0) + (result === "loss" ? 1 : 0),
    draws:   (current.draws || 0)  + (result === "draw" ? 1 : 0),
    streak:  newStreak,
    bestStreak: Math.max(newStreak, current.bestStreak || 0),
    history: [entry, ...(current.history || [])].slice(0, 50),
  });

  // What this result actually granted — returned so the UI can
  // celebrate drops without re-deriving them.
  const rewards = { trophyCard: null, streakDrops: [], title: null };

  // ── Depths achievements & reward grants ──────────────────────
  // Rank achievements key off PEAK elo (a rank, once reached, is earned
  // for good even if elo later dips). Win-count + streak milestones key
  // off the running totals. All idempotent via grantAchievement /
  // streakMilestonesHit / defeatedGatekeepers first-time checks.
  try {
    const totalWins = (current.wins || 0) + (result === "win" ? 1 : 0);
    if (newPeak >= 800)  await grantAchievement(uid, "terror:depths_champion",   "depths:rank");
    if (newPeak >= 2200) await grantAchievement(uid, "terror:depths_aenarchist", "depths:rank");
    if (newPeak >= 2800) await grantAchievement(uid, "terror:depths_solrend",    "depths:rank");
    if (totalWins >= 200) await grantAchievement(uid, "terror:depths_200_wins",  "depths:winCount");
    if (totalWins >= 500) await grantAchievement(uid, "terror:depths_500_wins",  "depths:winCount");
    if (newStreak >= 50)  await grantAchievement(uid, "terror:streak_50",        "depths:streak");

    // ── Gatekeeper defeat + TROPHY DROP ─────────────────────────
    // The live path runs through here (the standalone
    // recordGatekeeperDefeat is not wired into the UI). On a
    // gatekeeper WIN we persist the defeat, fire the achievement,
    // and — on FIRST defeat only — drop the trophy card in the
    // canonical holo_depths foil. Detection handles both legacy
    // ("gk_suhnker") and short ("suhnker") id formats.
    if (isGatekeeper && result === "win") {
      const gkId = ctx.gatekeeperId || opponentName;
      const prevGks = current.defeatedGatekeepers || [];
      const shortId = String(gkId).replace(/^gk_/, "");
      const firstDefeat = !prevGks.includes(gkId) && !prevGks.includes(shortId) && !prevGks.includes(`gk_${shortId}`);
      const updatedGks = [...new Set([...prevGks, gkId])];
      await update(ref(db, `profiles/${uid}/endlessDepths`), { defeatedGatekeepers: updatedGks });
      await grantAchievement(uid, "terror:gatekeeper_first", "depths:gatekeeper");
      if (firstDefeat) {
        const trophy = GATEKEEPER_TROPHIES[shortId];
        if (trophy?.trophyCardId) {
          rewards.trophyCard = trophy.trophyCardId;
          await addCardToCollection(uid, trophy.trophyCardId, true, RARITY.HOLO_DEPTHS, "depths:gatekeeper");
        }
      }
    }

    // ── STREAK MILESTONE DROPS ──────────────────────────────────
    // Crossing a threshold fires exactly once per profile (tracked in
    // streakMilestonesHit). Card milestones drop a random Escendant of
    // the pool's level in holo_depths; the 50 milestone grants the
    // "the Unbroken" profile title cosmetic instead.
    if (result === "win") {
      const hit = current.streakMilestonesHit || [];
      const newlyHit = STREAK_THRESHOLDS.filter(t => newStreak >= t.at && !hit.includes(`s${t.at}`));
      if (newlyHit.length > 0) {
        const updatedHits = [...hit, ...newlyHit.map(t => `s${t.at}`)];
        await update(ref(db, `profiles/${uid}/endlessDepths`), { streakMilestonesHit: updatedHits });
        for (const t of newlyHit) {
          const filter = STREAK_POOL_FILTERS[t.pool];
          if (filter?.titleOnly) {
            await grantCosmetic(uid, "title_the_unbroken", {
              source: "terror:depths_streak50", rarity: "mythic", slot: "title",
            });
            rewards.title = STREAK_50_TITLE;
          } else if (filter?.level) {
            const pool = getAllGrimoireCards().filter(c => c.type === CARD_TYPES.ESCENDANT && c.level === filter.level);
            if (pool.length > 0) {
              const drop = pool[Math.floor(Math.random() * pool.length)];
              await addCardToCollection(uid, drop.id, true, RARITY.HOLO_DEPTHS, `depths:streak${t.at}`);
              rewards.streakDrops.push({ at: t.at, cardId: drop.id, cardName: drop.name });
            }
          }
        }
      }
    }
  } catch (_) { /* non-fatal — never break a recorded result */ }

  return { newElo, newPeak, eloChange: delta, rewards };
}

// ── ENDLESS DEPTHS CREST REWARD ───────────────────────────────
// Call this after recordDepthResult to award DC for depth wins
export async function earnDepthCrests(uid, result, rankId, isGatekeeper) {
  if (result === "loss") return 0;

  let amount = 0;
  if (result === "draw") {
    amount = DEPTH_DC_REWARDS.draw;
  } else if (result === "win") {
    amount = DEPTH_DC_REWARDS[rankId] || DEPTH_DC_REWARDS.escling;
    if (isGatekeeper) amount *= DEPTH_DC_REWARDS.gatekeeperMult;
  }

  if (amount <= 0) return 0;

  // Delta-based transaction on the divineCrests leaf ONLY — never a
  // read-then-overwrite of the profile object (the write pattern that
  // caused the April 2026 data-loss incident).
  await runTransaction(ref(db, `profiles/${uid}/divineCrests`), (cur) => (cur || 0) + amount);
  return amount;
}

export async function recordGatekeeperDefeat(uid, gatekeeperId) {
  const snap = await get(ref(db, `profiles/${uid}/endlessDepths/defeatedGatekeepers`));
  const current = snap.exists() ? snap.val() : [];
  const updated = [...new Set([...current, gatekeeperId])];
  await update(ref(db, `profiles/${uid}/endlessDepths`), { defeatedGatekeepers: updated });
  // First gatekeeper ever felled. Idempotent; fires once.
  try { await grantAchievement(_uidKey(uid, "recordGatekeeperDefeat"), "terror:gatekeeper_first", "depths:gatekeeper"); }
  catch (_) { /* non-fatal */ }
}

// ═══════════════════════════════════════════════════════════════
// ENDLESS DEPTHS — TROPHIES, STREAKS, INNER CIRCLE
// ═══════════════════════════════════════════════════════════════
// Two layers of Depths rewards (Inner Circle removed July 2026 —
// it was never wired and added rules complexity for little payoff):
//   1. Gatekeeper Trophies — beat a gate boss, get a themed holo card
//   2. Streak Drops — consecutive wins trigger tiered card drops;
//      50-streak grants the "the Unbroken" profile title
// ═══════════════════════════════════════════════════════════════

// ── LAYER 1: GATEKEEPER TROPHIES ───────────────────────────────
// Canonical gatekeepers per Terror lore. Each gates a specific
// tier transition and drops a themed card as holo_depths.
//
// eloFloor    = minimum Elo required to challenge this gatekeeper
// tierBlocked = which rank they stand between
// trophyCardId = card that drops on first defeat (holo_depths)
export const GATEKEEPER_TROPHIES = {
  suhnker: {
    id:           "suhnker",
    name:         "S'uhnker",
    title:        "Sentinel of the Outer Gate",
    eloFloor:     400,
    tierBlocked:  "beb",
    trophyCardId: "premier_dragon_beb",
    trophyNote:   "A Dragon Beb, newly branded by the Depths.",
  },
  zhefir: {
    id:           "zhefir",
    name:         "Z'hefir",
    title:        "Keeper of Shadows",
    eloFloor:     800,
    tierBlocked:  "champion",
    trophyCardId: "spectral_tiger_lord",
    trophyNote:   "The apex predator of the Shadow Forest, bound in depth-ink.",
  },
  tyrent: {
    id:           "tyrent",
    name:         "T'yrent",
    title:        "The Last Flame",
    eloFloor:     1200,
    tierBlocked:  "lord",
    trophyCardId: "h01_lord",
    trophyNote:   "H-01 Super Nova — the Emperium's technological apex.",
  },
  vael: {
    id:           "vael",
    name:         "V'ael",
    title:        "The Inner Emperium",
    eloFloor:     1700,
    tierBlocked:  "escendant",
    trophyCardId: "xulan_lord",
    trophyNote:   "Xulan-Shadow — hatred rewarded with a depths holo.",
  },
  ruk: {
    id:           "ruk",
    name:         "Rük",
    title:        "Fracture Made Flesh",
    eloFloor:     2200,
    tierBlocked:  "aenarchist",
    trophyCardId: "chaos_dragon_lord",
    trophyNote:   "The Chaos Dragon Lord — the Depths' ultimate trophy.",
  },
};

// Get the next gatekeeper the player hasn't beaten
export const getNextGatekeeper = (defeatedIds = []) => {
  const defeated = new Set(defeatedIds);
  const ordered = ["suhnker", "zhefir", "tyrent", "vael", "ruk"];
  for (const id of ordered) {
    if (!defeated.has(id)) return GATEKEEPER_TROPHIES[id];
  }
  return null; // all beaten
};

// ── LAYER 2: STREAK DROPS ──────────────────────────────────────
// Hit a consecutive-win threshold → tiered card drops.
// Streak counter KEEPS GOING after drop (does not reset).
// Losing resets streak to 0.
// Milestones are absolute — hitting 7 means streaks 3 AND 7
// were both earned, but each fires once per Depths run.
export const STREAK_THRESHOLDS = [
  { at: 3,  pool: "beb",       note: "You're warming up."                },
  { at: 7,  pool: "champion",  note: "Something's clicking."             },
  { at: 12, pool: "lord",      note: "Now people are watching."          },
  { at: 20, pool: "lord",      note: "This is how legends begin."        },
  { at: 50, pool: "title",     note: "They said it couldn't be done."    },
];

// Pool definitions — which card tiers live in each streak pool.
// Resolved against the Escendant roster at runtime by level.
// (Recalibrated July 2026: "supreme" and "any/choice" pools removed —
//  Supremes are a board state, not collectible cards, and the picker
//  was complexity without payoff. Two layers now: gatekeepers + streaks.)
export const STREAK_POOL_FILTERS = {
  beb:      { level: 1 },            // L1 Escendants
  champion: { level: 2 },            // L2 Escendants
  lord:     { level: 3 },            // L3 Escendants
  title:    { titleOnly: true },     // Grants profile title cosmetic (no card)
};

// The 50-streak profile title
export const STREAK_50_TITLE = "the Unbroken";

// Determine Elo tier from Elo value
export const eloTier = (elo = 0) => {
  if (elo < 400)  return "escling";
  if (elo < 800)  return "beb";
  if (elo < 1200) return "champion";
  if (elo < 1700) return "lord";
  if (elo < 2200) return "escendant";
  if (elo < 2800) return "aenarchist";
  return "solrend";
};

// ═══════════════════════════════════════════════════════════════
// COMPANION SYSTEM
// ═══════════════════════════════════════════════════════════════
// Profile companion state lives at:
//   profiles/{uid}/companions/{companionId} = { unlockedAt, source }
//   profiles/{uid}/equippedCompanion        = companionId
//
// These functions operate on uid-keyed profiles (Plaza convention).
// ═══════════════════════════════════════════════════════════════

// CREST rewards for companion milestones
export const COMPANION_REWARDS = {
  TROPHY_UNLOCK: 50,
};

// ─────────────────────────────────────────────────────────────
// unlockCompanion — grant an explicit companion unlock
// Used for: story trophies, dungeon clears, depth ranks, secrets.
// Idempotent: calling twice on same companion returns
// { success: true, alreadyUnlocked: true }.
// ─────────────────────────────────────────────────────────────
export const unlockCompanion = async (uid, companionId, source = "unknown") => {
  if (!uid || !companionId) return { success: false };
  const companion = getCompanion(companionId);
  if (!companion) return { success: false, reason: "Unknown companion." };

  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return { success: false };
  const profile = snap.val();

  if (profile.companions && profile.companions[companionId]) {
    return { success: true, alreadyUnlocked: true };
  }

  const unlocks = { ...(profile.companions || {}) };
  unlocks[companionId] = { unlockedAt: Date.now(), source };

  await update(profileRef, { companions: unlocks });
  return { success: true, alreadyUnlocked: false, companion };
};

// ─────────────────────────────────────────────────────────────
// equipCompanion — set the active companion
// Charges the Sol Converter cost (varies by companion tier).
// Returns { success: false, reason, cost, have } on insufficient DC.
// ─────────────────────────────────────────────────────────────
export const equipCompanion = async (uid, companionId) => {
  if (!uid || !companionId) return { success: false };

  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return { success: false };
  const profile = snap.val();

  if (profile.equippedCompanion === companionId) {
    return { success: true, alreadyEquipped: true, cost: 0 };
  }

  if (!isCompanionUnlocked(profile, companionId)) {
    return { success: false, reason: "Companion is locked." };
  }

  const cost = getSolConverterCost(companionId);
  const have = profile.divineCrests || 0;
  if (have < cost) {
    return { success: false, reason: "Not enough Divine Crests.", cost, have };
  }

  await update(profileRef, {
    equippedCompanion: companionId,
    divineCrests: have - cost,
    [`crestHistory/${Date.now()}`]: { amount: -cost, reason: `Sol Convert: ${companionId}` },
  });

  return { success: true, cost, newBalance: have - cost };
};

// ─────────────────────────────────────────────────────────────
// ◆ RADIANT VERSIONS ◆
// buyRadiant   — purchase a (companion, tier) foil sheen. Append-only:
//                writes a single unlockedRadiants key, never overwrites.
// equipRadiant — set the active radiant sheen (or clear with null).
// ─────────────────────────────────────────────────────────────
export const buyRadiant = async (uid, companionId, tier) => {
  if (!uid || !companionId || !tier) return { success: false, reason: "Invalid radiant." };

  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return { success: false };
  const profile = snap.val();

  // Must own the companion to dress it.
  if (!isCompanionUnlocked(profile, companionId)) {
    return { success: false, reason: "You don't own that companion yet." };
  }

  const key = radiantKey(companionId, tier);
  if (profile.unlockedRadiants && profile.unlockedRadiants[key]) {
    return { success: true, alreadyOwned: true };
  }

  const cost = getRadiantCost(tier);
  const have = profile.divineCrests || 0;
  if (have < cost) return { success: false, reason: "Not enough Divine Crests.", cost, have };

  await update(profileRef, {
    divineCrests: have - cost,
    [`unlockedRadiants/${key}`]: { at: Date.now(), companionId, tier },
    [`crestHistory/${Date.now()}`]: { amount: -cost, reason: `Radiant: ${companionId} ${tier}` },
  });

  return { success: true, cost, newBalance: have - cost };
};

// Set (or clear) the radiant sheen worn by the equipped companion.
// Pass tier=null to remove. Validates ownership of the pairing.
export const equipRadiant = async (uid, companionId, tier) => {
  if (!uid) return { success: false };
  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return { success: false };
  const profile = snap.val();

  if (tier === null) {
    await update(profileRef, { equippedCompanionRadiant: null });
    return { success: true, cleared: true };
  }

  const key = radiantKey(companionId, tier);
  if (!(profile.unlockedRadiants && profile.unlockedRadiants[key])) {
    return { success: false, reason: "Radiant not owned." };
  }

  await update(profileRef, {
    equippedCompanion: companionId,
    equippedCompanionRadiant: tier,
  });

  // ── Radiant achievements ──────────────────────────────────────
  // radiant_first: equipping any radiant sheen. three_radiant: having
  // unlocked radiants across 3 DISTINCT companions (Radiant Trinity).
  try {
    const u = _uidKey(uid, "equipRadiant");
    await grantAchievement(u, "terror:radiant_first", "radiant:equip");
    const owned = profile.unlockedRadiants || {};
    const distinctCompanions = new Set(
      Object.values(owned).map((r) => r && r.companionId).filter(Boolean)
    );
    if (distinctCompanions.size >= 3) {
      await grantAchievement(u, "terror:three_radiant", "radiant:trinity");
    }
  } catch (_) { /* non-fatal */ }

  return { success: true, tier };
};

// ─────────────────────────────────────────────────────────────
// ensureStarterCompanion — one-time migration helper
// On login, if the player has a starterDeckId but no equipped
// companion, auto-equip their starter Beb for free and record
// the unlock. Safe to call on every login (idempotent).
// ─────────────────────────────────────────────────────────────
export const ensureStarterCompanion = async (uid) => {
  if (!uid) return { success: false };
  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return { success: false };
  const profile = snap.val();

  if (profile.equippedCompanion) return { success: true, noChange: true };
  if (!profile.starterDeckId)    return { success: true, noChange: true };

  const starterCompanionId = getStarterCompanionId(profile.starterDeckId);
  if (!starterCompanionId) return { success: false };

  await update(profileRef, {
    equippedCompanion: starterCompanionId,
    [`companions/${starterCompanionId}`]: { unlockedAt: Date.now(), source: "starter" },
  });

  return { success: true, companionId: starterCompanionId };
};

// ─────────────────────────────────────────────────────────────
// backfillStoryTrophies — retroactive trophy unlock
// Walks profile.storyProgress.defeatedOpponents and grants any
// trophy companions the player earned before the companion
// system existed. Idempotent; safe to call on every login.
// ─────────────────────────────────────────────────────────────
export const backfillStoryTrophies = async (uid) => {
  if (!uid) return;
  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return;
  const profile = snap.val();

  const defeated = profile.storyProgress?.defeatedOpponents || [];
  const current  = profile.companions || {};
  const updates  = {};
  let added = 0;

  // Story trophy mapping — defeating these opponents grants a trophy companion
  const trophyMap = {
    suhnker: "suhnker_trophy",
    zhefir:  "zhefir_trophy",
    tyrent:  "tyrent_trophy",
    jynxi:   "jynxi",
  };

  for (const opponentId of defeated) {
    const trophyId = trophyMap[opponentId];
    if (!trophyId) continue;
    if (current[trophyId]) continue;
    updates[`companions/${trophyId}`] = { unlockedAt: Date.now(), source: `backfill:${opponentId}` };
    added++;
  }

  if (added > 0) await update(profileRef, updates);
  return { success: true, added };
};

// ════════════════════════════════════════════════════════════════════
// IE CURRENCY — TERROR READ-ONLY SLICE
// ════════════════════════════════════════════════════════════════════
//
// ⚠ EXCHANGES DO NOT HAPPEN IN TERROR ⚠
//
// Per IE team alignment (Apr 2026): currency exchange is Pierre's
// exclusive responsibility. The Crest Well at W'ily's Pier is the
// ONLY place DC ↔ SE conversion happens in the Illegal Empire.
//
// If you're about to add an exchange modal to Terror — stop. Send
// the player to unstable-pierre.pages.dev via the blue portal.
//
// Terror is allowed to READ any currency balance for display
// (e.g., showing SE Coins in the header HUD), but must NEVER write
// to snakeEyesCoins, exchangeHistory, or lastExchangeAt. Those
// paths belong to Pierre + Snake Eyes.
//
// This slim block provides only the read helpers + display utilities
// Terror needs. The full exchange API (performExchange, previewExchange,
// cooldowns, rates, grants) lives in Pierre's and Snake Eyes' copies
// of progression.js — deliberately NOT here.
//
// Writing Divine Crests for match rewards: use Terror's existing
// `awardCrests` function (defined earlier in this file), NOT grantCurrency.
// ════════════════════════════════════════════════════════════════════

// ── CURRENCY REGISTRY (for display labels / icons) ────────────────────
// Terror uses this to render "◆ DC" and "◈ SE" labels in UI.
// Keep shape identical to Pierre's / Snake Eyes' copies — this is the
// canonical IE currency registry.
export const CURRENCIES = {
  DC: {
    id:    "DC",
    name:  "Divine Crests",
    short: "DC",
    path:  "divineCrests",
    icon:  "◆",
  },
  SE: {
    id:    "SE",
    name:  "Snake Eyes Coins",
    short: "SE",
    path:  "snakeEyesCoins",
    icon:  "◈",
  },
};

// ── BALANCE READERS ───────────────────────────────────────────────────
// Read any IE currency balance for a profile. Read-only — Terror never
// writes to balance paths via these; DC writes go through awardCrests/
// spendCrests, and SE writes are Snake Eyes' responsibility.

export const getBalance = async (uid, currencyId) => {
  const curr = CURRENCIES[currencyId];
  if (!curr) throw new Error(`Unknown currency: ${currencyId}`);
  const snap = await get(ref(db, `profiles/${uid}/${curr.path}`));
  return snap.val() || 0;
};

export const listenToBalance = (uid, currencyId, callback) => {
  const curr = CURRENCIES[currencyId];
  if (!curr) throw new Error(`Unknown currency: ${currencyId}`);
  return onValue(
    ref(db, `profiles/${uid}/${curr.path}`),
    (snap) => callback(snap.val() || 0)
  );
};

// ── DISPLAY FORMATTERS ────────────────────────────────────────────────
// Pure utilities — shared shape with Pierre / Snake Eyes so the same
// "◆ 1.2k" renders identically across every IE app.

export const formatCurrency = (amount, currencyId = null) => {
  const n = Math.max(0, Math.floor(amount || 0));
  let formatted;
  if (n >= 1_000_000)  formatted = (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  else if (n >= 1_000) formatted = (n / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  else                 formatted = String(n);

  if (!currencyId) return formatted;
  const curr = CURRENCIES[currencyId];
  return curr ? `${curr.icon} ${formatted}` : formatted;
};

// ════════════════════════════════════════════════════════════════════
// END OF IE CURRENCY READ-ONLY SLICE
// ════════════════════════════════════════════════════════════════════


// ════════════════════════════════════════════════════════════════════
// IE COSMETICS — SHARED IDENTITY SLICE
// ════════════════════════════════════════════════════════════════════
//
// ⚠ EQUIP-ANYWHERE LANE RULE ⚠
//
// Per IE Cosmetic Schema v1.1 (Apr 2026): cosmetics are the INVERSE of
// currency. Any IE game can grant unlocks. Any IE game can equip. All
// IE games render the equipped state immediately via listenToEquipped.
//
// The Pier is the DISCOVERY/ORGANIZATION hub (the Wardrobe) — it is
// NOT an equipping chokepoint. You equip wherever you earned it.
//
// This block is safe to paste VERBATIM into:
//   - Terror's   progression.js
//   - Pierre's   progression.js
//   - Snake Eyes' progression.js
//
// Shape MUST stay identical across all three copies. If you change
// this file, update IE_COSMETIC_SCHEMA_v1.md first, bump the version,
// then propagate to all three apps in the same session.
//
// Canonical reference: IE_COSMETIC_SCHEMA_v1.md
// ════════════════════════════════════════════════════════════════════

// ── COSMETIC SLOTS ────────────────────────────────────────────────────
// Six fixed slots, one item per slot. Locked by schema v1.1.
// If you need a "particle trail + glow halo + aura" combo, those are
// sub-effects of the `aura` slot — do NOT add new top-level slots.

export const COSMETIC_SLOTS = ["head", "face", "body", "back", "aura", "title"];

// ── RARITY TIERS ──────────────────────────────────────────────────────
// Six tiers. Rarity is metadata — each game interprets it with its own
// aesthetic (Terror: manuscript-ink legendary; Casino: neon holo; etc.)

export const COSMETIC_RARITIES = [
  "common", "uncommon", "rare", "epic", "legendary", "mythic",
];

// Display metadata — shared so "Epic" and "Mythic" feel consistent
// across every IE app even if their visual treatment differs.
export const RARITY_META = {
  common:    { order: 0, label: "Common",    short: "C",  tint: "#9aa4ad" },
  uncommon:  { order: 1, label: "Uncommon",  short: "U",  tint: "#4da36a" },
  rare:      { order: 2, label: "Rare",      short: "R",  tint: "#4a88c8" },
  epic:      { order: 3, label: "Epic",      short: "E",  tint: "#8a5cd6" },
  legendary: { order: 4, label: "Legendary", short: "L",  tint: "#d4a84b" },
  mythic:    { order: 5, label: "Mythic",    short: "M",  tint: "#c94a6b" },
};

// ── VALIDATION HELPERS (internal) ─────────────────────────────────────

const _validSlot   = (slot)   => COSMETIC_SLOTS.includes(slot);
const _validRarity = (rarity) => COSMETIC_RARITIES.includes(rarity);

// ── GRANT ─────────────────────────────────────────────────────────────
// Award a cosmetic unlock. Append-only: if already unlocked, this is a
// no-op — original unlockedAt and source are preserved.
//
// meta: { source: "terror:...", rarity: "legendary", slot: "head" }
//   - source: namespaced string identifying where/why it was earned
//   - rarity: one of COSMETIC_RARITIES
//   - slot:   one of COSMETIC_SLOTS (the slot this cosmetic occupies)
//
// Returns: { success, alreadyUnlocked, reason? }

export const grantCosmetic = async (uid, cosmeticId, meta = {}) => {
  if (!uid || !cosmeticId) {
    return { success: false, reason: "Missing uid or cosmeticId." };
  }
  const { source, rarity, slot } = meta;
  if (!_validSlot(slot)) {
    return { success: false, reason: `Invalid slot: ${slot}` };
  }
  if (!_validRarity(rarity)) {
    return { success: false, reason: `Invalid rarity: ${rarity}` };
  }
  if (!source || typeof source !== "string") {
    return { success: false, reason: "Missing or invalid source." };
  }

  const cosmeticRef = ref(db, `profiles/${uid}/unlockedCosmetics/${cosmeticId}`);
  const snap = await get(cosmeticRef);

  if (snap.exists()) {
    // Append-only: never overwrite. Provenance is permanent.
    return { success: true, alreadyUnlocked: true };
  }

  await set(cosmeticRef, {
    unlockedAt: Date.now(),
    source,
    rarity,
    slot,
  });

  return { success: true, alreadyUnlocked: false };
};

// ── EQUIP / UNEQUIP ───────────────────────────────────────────────────
// Equip a cosmetic to its slot. Verifies the player owns it first, and
// that the stored slot matches the target slot (prevents ID collisions
// from cross-app catalogs silently equipping to the wrong slot).
//
// Returns: { success, reason? }

export const equipCosmetic = async (uid, slot, cosmeticId) => {
  if (!uid || !slot || !cosmeticId) {
    return { success: false, reason: "Missing uid, slot, or cosmeticId." };
  }
  if (!_validSlot(slot)) {
    return { success: false, reason: `Invalid slot: ${slot}` };
  }

  const unlockSnap = await get(ref(db, `profiles/${uid}/unlockedCosmetics/${cosmeticId}`));
  if (!unlockSnap.exists()) {
    return { success: false, reason: "Not unlocked." };
  }

  const unlock = unlockSnap.val();
  if (unlock.slot !== slot) {
    return {
      success: false,
      reason: `Slot mismatch: ${cosmeticId} belongs in "${unlock.slot}", not "${slot}".`,
    };
  }

  // Check if this is the first cosmetic ever equipped
  const equippedRef = ref(db, `profiles/${uid}/equipped`);
  const equippedSnap = await get(equippedRef);
  const currentEquipped = equippedSnap.val() || {};
  const hasAnyEquipped = Object.values(currentEquipped).some(v => v !== null);

  await update(equippedRef, { [slot]: cosmeticId });
  
  // First cosmetic achievement
  if (!hasAnyEquipped) {
    await grantAchievement(uid, "terror:cosmetic_first", "wardrobe:equip");
  }

  return { success: true };
};

// Unequip a slot (equip null).
export const unequipSlot = async (uid, slot) => {
  if (!uid || !_validSlot(slot)) {
    return { success: false, reason: `Invalid slot: ${slot}` };
  }
  await update(ref(db, `profiles/${uid}/equipped`), { [slot]: null });
  return { success: true };
};

// Unequip every slot at once (useful for "reset to default" UI).
export const unequipAll = async (uid) => {
  if (!uid) return { success: false };
  const cleared = {};
  for (const s of COSMETIC_SLOTS) cleared[s] = null;
  await update(ref(db, `profiles/${uid}/equipped`), cleared);
  return { success: true };
};

// ── READ HELPERS ──────────────────────────────────────────────────────

// One-shot read of all equipped slots. Returns a plain object with all
// six slots populated (values are cosmeticId strings or null).
export const getEquipped = async (uid) => {
  const empty = Object.fromEntries(COSMETIC_SLOTS.map((s) => [s, null]));
  if (!uid) return empty;
  const snap = await get(ref(db, `profiles/${uid}/equipped`));
  const val = snap.val() || {};
  return { ...empty, ...val };
};

// Live listener for equipped state. Callback fires with the normalized
// { head, face, body, back, aura, title } object on every change.
// Returns the unsubscribe function.
export const listenToEquipped = (uid, callback) => {
  const empty = Object.fromEntries(COSMETIC_SLOTS.map((s) => [s, null]));
  return onValue(ref(db, `profiles/${uid}/equipped`), (snap) => {
    callback({ ...empty, ...(snap.val() || {}) });
  });
};

// One-shot read of the full unlock map.
// Shape: { [cosmeticId]: { unlockedAt, source, rarity, slot } }
export const getUnlocked = async (uid) => {
  if (!uid) return {};
  const snap = await get(ref(db, `profiles/${uid}/unlockedCosmetics`));
  return snap.val() || {};
};

// Live listener for the full unlock map.
export const listenToUnlocked = (uid, callback) => {
  return onValue(ref(db, `profiles/${uid}/unlockedCosmetics`), (snap) => {
    callback(snap.val() || {});
  });
};

// Quick check: does this uid own this cosmetic?
export const isUnlocked = async (uid, cosmeticId) => {
  if (!uid || !cosmeticId) return false;
  const snap = await get(ref(db, `profiles/${uid}/unlockedCosmetics/${cosmeticId}`));
  return snap.exists();
};

// ── SOURCE/PROVENANCE QUERIES ─────────────────────────────────────────
// These operate on an already-fetched unlock map so callers don't pay
// for redundant Firebase reads when they already have the data.

// Filter unlocks to those earned in a given app namespace.
// appPrefix: "terror" | "casino" | "pier" | "empire" | ...
export const filterUnlocksByApp = (unlockMap, appPrefix) => {
  const out = {};
  const prefix = `${appPrefix}:`;
  for (const [id, entry] of Object.entries(unlockMap || {})) {
    if (typeof entry?.source === "string" && entry.source.startsWith(prefix)) {
      out[id] = entry;
    }
  }
  return out;
};

// Filter unlocks to those matching a slot.
export const filterUnlocksBySlot = (unlockMap, slot) => {
  const out = {};
  for (const [id, entry] of Object.entries(unlockMap || {})) {
    if (entry?.slot === slot) out[id] = entry;
  }
  return out;
};

// Filter unlocks to those matching a rarity.
export const filterUnlocksByRarity = (unlockMap, rarity) => {
  const out = {};
  for (const [id, entry] of Object.entries(unlockMap || {})) {
    if (entry?.rarity === rarity) out[id] = entry;
  }
  return out;
};

// Sort unlocks most-recent-first. Returns an array of [id, entry] tuples.
export const sortUnlocksByRecency = (unlockMap) => {
  return Object.entries(unlockMap || {})
    .sort((a, b) => (b[1]?.unlockedAt || 0) - (a[1]?.unlockedAt || 0));
};

// Sort unlocks by rarity (rarest first). Returns [id, entry] tuples.
export const sortUnlocksByRarity = (unlockMap) => {
  return Object.entries(unlockMap || {}).sort((a, b) => {
    const ra = RARITY_META[a[1]?.rarity]?.order ?? -1;
    const rb = RARITY_META[b[1]?.rarity]?.order ?? -1;
    return rb - ra;
  });
};

// ════════════════════════════════════════════════════════════════════
// END OF IE COSMETICS — SHARED IDENTITY SLICE
// ════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────
// COMPANION PETTING — Session-Scoped Achievement Tracking
// ─────────────────────────────────────────────────────────────
// Tracks cumulative pet counts for milestones. Called from
// CompanionInteraction.onPet callback during each petting session.
// Fires achievements at 100, 500 pet milestones (idempotent).

export const recordCompanionPet = async (uid, sessionPets) => {
  // sessionPets is the tap count this session (from CompanionInteraction state)
  // We increment the profile's total and check milestones.
  if (!uid || !sessionPets) return;

  const profileRef = ref(db, `profiles/${uid}`);
  const snap = await get(profileRef);
  if (!snap.exists()) return;
  
  const profile = snap.val();
  const currentTotal = (profile.totalPets || 0) + sessionPets;
  
  // Write cumulative count
  await update(profileRef, { totalPets: currentTotal });

  // Check achievement milestones (idempotent due to grantAchievement)
  if (currentTotal >= 100) {
    await grantAchievement(uid, "terror:pet_100", "companion:pet");
  }
  if (currentTotal >= 500) {
    await grantAchievement(uid, "terror:pet_500", "companion:pet");
  }
};


// ─────────────────────────────────────────────────────────────
// ROOM BACKDROPS — the Backdrop Atelier economy (July 8, 2026)
// ─────────────────────────────────────────────────────────────
// Schema (NEW node — add to the hardening-session whitelist):
//   profiles/{uid}/roomBackdrops/
//     equipped : string (backdrop id; catalog lives in
//                engine/RoomBackdrop.js — code is the catalog truth)
//     unlocked : { [id]: { source, ts } }   ← APPEND-ONLY
//
// Laws honored:
//   • Spend path is the canonical atomic spendCrests transaction —
//     no read-then-write, no double-spend, Terror writes DC only.
//   • Unlock is a transaction-MERGE (current || grant): append-only,
//     provenance-stamped, never overwrites an existing unlock.
//   • GRANT-BEFORE-FLAG: the unlock IS the grant; `equipped` is a
//     mere pointer written separately — a failed equip can never
//     burn a purchase, and re-calling unlock on an owned backdrop
//     is a no-op that never re-charges.
// ─────────────────────────────────────────────────────────────

export const unlockBackdrop = async (uid, backdropId, cost = 0, source = "atelier_purchase") => {
  uid = _uidKey(uid, "unlockBackdrop");
  if (!uid || !backdropId) return { ok: false, reason: "bad-args" };

  const ownRef = ref(db, `profiles/${uid}/roomBackdrops/unlocked/${backdropId}`);

  // Already owned? Never charge twice.
  const snap = await get(ownRef);
  if (snap.exists()) return { ok: true, reason: "already-owned" };

  // Charge — atomic check-and-debit; aborts untouched if short.
  if (cost > 0) {
    const paid = await spendCrests(uid, cost);
    if (!paid) return { ok: false, reason: "insufficient-crests" };
  }

  // Grant — append-only merge; preserves any concurrent grant's provenance.
  await runTransaction(ownRef, (current) => current || { source, ts: Date.now() });
  return { ok: true, reason: "purchased" };
};

export const equipBackdrop = async (uid, backdropId) => {
  uid = _uidKey(uid, "equipBackdrop");
  if (!uid || !backdropId) return false;
  await set(ref(db, `profiles/${uid}/roomBackdrops/equipped`), backdropId);
  return true;
};
